'use client';

import { useRecoilValue } from 'recoil';
import { isProjectCompleteState, selectedProjectDataState, wlSupplyState } from '@/lib/state';
import { ABSOLUTE_MIN_BID } from '@/lib/constants';
import { lamportsToToken } from '@/lib/utils';
import ProjectStatsSkeleton from '@/components/ProjectStatsSkeleton';
import ProjectStat from '@/components/ProjectStat';

export default function ProjectStatistics() {
  const project = useRecoilValue(selectedProjectDataState);
  const isProjectComplete = useRecoilValue(isProjectCompleteState);
  const wlSupply = useRecoilValue(wlSupplyState);

  if (!project) {
    return <ProjectStatsSkeleton />;
  }

  const { supply, min_bid, outbid_increment, token, decimals } = project;
  const minInSol = lamportsToToken(decimals, min_bid || 0);
  const oiSol = lamportsToToken(decimals, outbid_increment || 0);

  return (
    <div className="flex items-center justify-between">
      <div className="flex font-light flex-wrap md:flex-nowrap">
        <div
          className="tooltip tooltip-bottom cursor-pointer mr-2 mb-2"
          data-tip="Total items, including those not for sale on BidHub"
        >
          <ProjectStat>
            Total Supply: <span className="font-bold">{Number(supply).toLocaleString()}</span>
          </ProjectStat>
        </div>
        {wlSupply && wlSupply > 0 ? (
          <div
            className="tooltip tooltip-bottom cursor-pointer mb-2 mr-2"
            data-tip="Maximum possible bid slots claimable by WL holders"
          >
            <ProjectStat>
              WL Supply: <span className="font-bold">{Number(wlSupply).toLocaleString()}</span>
            </ProjectStat>
          </div>
        ) : null}
        <div
          className="tooltip tooltip-bottom cursor-pointer mr-2 mb-2"
          data-tip="Once sold out, new bids must exceed the lowest live winning bid by at least this amount."
        >
          <ProjectStat>
            Outbid Step: <span className="font-bold">{Number(oiSol).toLocaleString()}</span>{' '}
            {token === 'SOL' ? '◎' : token}
          </ProjectStat>
        </div>
        {isProjectComplete ? (
          <div className="mb-2 mr-2">
            <ProjectStat>Complete</ProjectStat>
          </div>
        ) : (
          <div className="mb-2 mr-2">
            <ProjectStat>
              {min_bid ? (
                <div
                  className="tooltip tooltip-bottom cursor-pointer"
                  data-tip="Minimum starting bid; the live winning bid threshold will be higher once bid count reaches 100% of supply"
                >
                  Starting Bid:{' '}
                  <span className="font-bold">{Number(minInSol).toLocaleString()}</span>{' '}
                  {token === 'SOL' ? '◎' : token}
                </div>
              ) : (
                <div
                  className="tooltip tooltip-bottom cursor-pointer"
                  data-tip={`BidHub's absolute minimum bid size is ${ABSOLUTE_MIN_BID} lamports`}
                >
                  <p>No Minimum Bid</p>
                </div>
              )}
            </ProjectStat>
          </div>
        )}
      </div>
    </div>
  );
}
